/**
 * SystemEditor — PIM Admin interface with 3 tabs:
 * 1. Marketing (texts, photos, video)
 * 2. Technical (motors, protocols, power, colors)
 * 3. Measurement (min/max dimensions)
 * 
 * Quiet Luxury design: glassmorphism, minimal UI, smooth transitions
 */
import { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, Barcode, DollarSign, Ruler, RefreshCw, ExternalLink, Copy, AlertCircle } from 'lucide-react';
import { useHaptic } from '@/hooks/useHaptic';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  PIMMeasurementParams,
  getPIMSystemMeasurement,
  updatePIMSystemMeasurement,
  duplicatePIMSystem,
  invalidateCacheTag,
  AdminCatalogSystem,
} from '@/api';
import api from '@/api';
import { MarketingTab } from './MarketingTab';
import { MeasurementTab } from './MeasurementTab';
import { PricingTab } from './PricingTab';
import { SKUTab } from './SKUTab';

interface PriceTier {
  id?: number;
  tier_label: string;
  tier_type: string;
  width_min_cm: number | null;
  width_max_cm: number | null;
  height_min_cm: number | null;
  height_max_cm: number | null;
  base_price: number;
  price_per_cm_width: number | null;
  price_per_cm_height: number | null;
  is_active: boolean;
}

interface SystemVariant {
  id?: number;
  variant_code: string;
  sku: string;
  motor_id: number | null;
  width_min_m: number | null;
  width_max_m: number | null;
  height_min_m: number | null;
  height_max_m: number | null;
  price_adjustment: number;
  is_default: boolean;
  is_active: boolean;
}

export type SystemEditorTab = 'marketing' | 'sku' | 'pricing' | 'measurement';

export interface SystemEditorProps {
  system: AdminCatalogSystem | null;
  isOpen: boolean;
  onClose: () => void;
  onSave?: (system: AdminCatalogSystem) => void;
  onDuplicate?: (newSystem: { id: number; slug: string; title: string }) => void;
}

const TABS = [
  { id: 'marketing' as SystemEditorTab, label: 'Маркетинг', icon: Package },
  { id: 'sku' as SystemEditorTab, label: 'Артикулы', icon: Barcode },
  { id: 'pricing' as SystemEditorTab, label: 'Цены', icon: DollarSign },
  { id: 'measurement' as SystemEditorTab, label: 'Замеры', icon: Ruler },
] as const;

export function SystemEditor({ system, isOpen, onClose, onSave, onDuplicate }: SystemEditorProps) {
  const haptic = useHaptic();
  const [activeTab, setActiveTab] = useState<SystemEditorTab>('marketing');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Data states
  const [measurement, setMeasurement] = useState<PIMMeasurementParams | null>(null);
  
  // Pricing data states
  const [priceTiers, setPriceTiers] = useState<PriceTier[]>([]);
  const [variants, setVariants] = useState<SystemVariant[]>([]);
  
  // Load data when tab changes
  const loadMeasurement = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getPIMSystemMeasurement(system!.id);
      setMeasurement(data);
    } catch (e) {
      setError('Ошибка загрузки параметров замера');
    } finally {
      setIsLoading(false);
    }
  }, [system?.id]);
  
  useEffect(() => {
    if (!isOpen) return;
    if (activeTab === 'measurement') {
      void loadMeasurement();
    } else if (activeTab === 'pricing') {
      void loadPricing();
    }
  }, [isOpen, activeTab, loadMeasurement]);
  
  // Reset state when system changes
  useEffect(() => {
    if (!isOpen) {
      setMeasurement(null);
      setPriceTiers([]);
      setVariants([]);
      setError(null);
      setActiveTab('marketing');
    }
  }, [isOpen, system?.id]);

  // Load pricing data (price tiers and variants)
  const loadPricing = useCallback(async () => {
    setIsLoading(true);
    try {
      const [tiersRes, variantsRes] = await Promise.all([
        api.get<PriceTier[]>(`/v3/admin/systems/${system!.id}/price-tiers`).then(r => r.data).catch(() => []),
        api.get<SystemVariant[]>(`/v3/admin/systems/${system!.id}/variants`).then(r => r.data).catch(() => []),
      ]);
      setPriceTiers(Array.isArray(tiersRes) ? tiersRes : []);
      setVariants(Array.isArray(variantsRes) ? variantsRes : []);
    } catch {
      setError('Ошибка загрузки цен и вариантов');
    } finally {
      setIsLoading(false);
    }
  }, [system?.id]);
  
  // Actions
  const handleDuplicate = async () => {
    try {
      const newSystem = await duplicatePIMSystem(system!.id);
      haptic.success();
      onDuplicate?.(newSystem);
      onClose();
    } catch (e) {
      setError('Ошибка дублирования системы');
    }
  };
  
  const handleInvalidateCache = async () => {
    try {
      await invalidateCacheTag(`systems:${system!.slug}`);
      haptic.success();
    } catch {
      setError('Ошибка инвалидации кэша');
    }
  };

  const handleSaveMeasurement = async (params: PIMMeasurementParams) => {
    setIsSaving(true);
    try {
      await updatePIMSystemMeasurement(system!.id, params);
      haptic.success();
      setMeasurement(params);
    } catch (e) {
      setError('Ошибка сохранения параметров');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Save price tiers (bulk PUT — fallback for full replace)
  const handleSavePriceTiers = async (tiers: PriceTier[]) => {
    setIsSaving(true);
    try {
      await api.put(`/v3/admin/systems/${system!.id}/price-tiers`, tiers);
      haptic.success();
      setPriceTiers(tiers);
      await invalidateCacheTag(`systems:${system!.slug}`).catch(() => null);
    } catch (e) {
      throw e;
    } finally {
      setIsSaving(false);
    }
  };

  // Save variants
  const handleSaveVariants = async (newVariants: SystemVariant[]) => {
    setIsSaving(true);
    try {
      await api.put(`/v3/admin/systems/${system!.id}/variants`, newVariants);
      haptic.success();
      setVariants(newVariants);
    } catch (e) {
      throw e;
    } finally {
      setIsSaving(false);
    }
  };
  
  if (!system) return null;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="bottom"
        className="h-[94dvh] w-full max-w-full rounded-t-2xl p-0 flex flex-col border-t border-white/10"
      >
        <SheetHeader className="px-4 py-3 border-b border-white/10 pr-12">
          <div className="flex items-start justify-between gap-2">
            <div className="flex flex-col min-w-0">
              <SheetTitle className="text-base font-semibold text-white leading-tight truncate">
                {system.title}
              </SheetTitle>
              <span className="text-[10px] text-white/40 truncate">{system.slug}</span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={handleInvalidateCache}
                title="Сбросить кэш"
                className="p-2 rounded-lg text-xs transition-colors hover:bg-white/10 text-white/50 hover:text-white/80"
              >
                <RefreshCw size={14} />
              </button>
              <a
                href={`/configurator?system=${system.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Превью в конфигураторе"
                className="p-2 rounded-lg text-xs transition-colors hover:bg-white/10 text-white/50 hover:text-white/80"
              >
                <ExternalLink size={14} />
              </a>
              <button
                onClick={handleDuplicate}
                title="Дублировать"
                className="p-2 rounded-lg text-xs transition-colors hover:bg-white/10 text-white/70"
              >
                <Copy size={14} />
              </button>
            </div>
          </div>
          <SheetDescription className="text-white/40 text-[11px] mt-0.5">
            #{system.id} • {system.category}
          </SheetDescription>
        </SheetHeader>
        
        {/* Tabs */}
        <div className="flex gap-0.5 px-3 pt-3 border-b border-white/10 overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-all relative rounded-t-lg whitespace-nowrap flex-shrink-0 ${
                  isActive ? 'text-[#68D391]' : 'text-white/50 hover:bg-white/5 hover:text-white/70'
                }`}>
                <Icon size={13} />
                {tab.label}
                {isActive && (
                  <motion.div
                    layoutId="systemEditorTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-[#68D391]"
                  />
                )}
              </button>
            );
          })}
        </div>
        
        {/* Error banner */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="flex items-center gap-2 px-6 py-2 text-xs bg-orange-500/10 text-orange-400"
            >
              <AlertCircle size={14} />
              {error}
              <button onClick={() => setError(null)} className="ml-auto hover:text-orange-300">×</button>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Content */}
        <div className="flex-1 p-4 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-sm text-white/50">
              Загрузка...
            </div>
          ) : (
            <>
              {activeTab === 'marketing' && (
                <MarketingTab system={system} onSave={onSave} />
              )}
              {activeTab === 'pricing' && (
                <PricingTab
                  system={system}
                  priceTiers={priceTiers}
                  variants={variants}
                  onSavePriceTiers={handleSavePriceTiers}
                  onSaveVariants={handleSaveVariants}
                  isSaving={isSaving}
                />
              )}
              {activeTab === 'measurement' && measurement && (
                <MeasurementTab
                  params={measurement}
                  onSave={handleSaveMeasurement}
                  isSaving={isSaving}
                />
              )}
              {activeTab === 'sku' && (
                <SKUTab system={system} />
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-white/10">
          <div className="text-xs text-white/40">
            ID: {system.id} • {system.tenant_id?.slice(0, 8)}...
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm font-medium transition-colors bg-white/10 text-white/80 hover:bg-white/20"
          >
            Закрыть
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
